// Constants
import { noRanking, allowedStatuses, ordinalOffset } from "../constants";

const findCommissionCycleNodeInTree = (tree, id) => {
	for (const year of tree) {
		for (const month of year.list) {
			if (month._id === id) {
				return {
					year,
					month,
				};
			}
		}
	}
	return null;
};

function expandActiveCommissionCycle(tree, commissionCycles, toggleTitle) {
	const expanded = [];
	const closingCommissionCycles = commissionCycles.filter(
		(CC) => CC.status.toUpperCase() === "CLOSING" || CC.status.toUpperCase() === "POSTED"
	);
	const activeCommissionCycles = commissionCycles.filter((CC) => CC.status.toUpperCase() === "ACTIVE");

	const date = new Date();
	const dateYear = date.getFullYear();
	const dateMonth = date.getMonth();
	const dateDay = date.getDate();
	const monthTitles = [
		"January",
		"February",
		"March",
		"April",
		"May",
		"June",
		"July",
		"August",
		"September",
		"October",
		"November",
		"December",
	];
	if (toggleTitle !== "By Employee") {
		return [...new Set(expanded)];
	}

	[...closingCommissionCycles, ...activeCommissionCycles].forEach((commissionCycle) => {
		const commissionCycleNode = findCommissionCycleNodeInTree(tree, commissionCycle._id);
		const commissionCycleYear = String(commissionCycleNode.year.name).trim();
		const commissionCycleMonth = String(commissionCycleNode.month.name).trim();
		expanded.push(commissionCycleNode.year._id);
		expanded.push(commissionCycleNode.month._id);
		if (
			commissionCycles === activeCommissionCycles &&
			String(commissionCycleYear) === String(dateYear) &&
			commissionCycleMonth === monthTitles[dateMonth]
		) {
			for (const payPeriod of commissionCycleNode.month.list) {
				let payPeriodTitle = payPeriod.name.split(" ")[0]; //1st-16th Semi-monthly -> 1st-16th
				payPeriodTitle = payPeriodTitle.split("-");
				const payPeriodStart = +payPeriodTitle[0].replace("/[^0-9]/g", ""); //1st -> 1
				const payPeriodEnd = +payPeriodTitle[1].replace("/[^0-9]/g", ""); //16th -> 16
				if (dateDay >= payPeriodStart && dateDay <= payPeriodEnd) {
					expanded.push(payPeriod._id);
				}
			}
		} else {
			const payPeriod = commissionCycleNode.month.list[commissionCycleNode?.month?.list?.length - 1 || 0];
			expanded.push(payPeriod._id);
		}
	});
	return [...new Set(expanded)];
}

function sortCollectionBy(collection, args) {
	const { sortBy = null, order = "ASC", parseToNumber = false } = args;
	if (collection.length && sortBy) {
		collection.sort((elementA, elementB) => {
			if (sortBy.includes("date") || sortBy.includes("Date")) {
				return new Date(elementA[sortBy]).getTime() > new Date(elementB[sortBy]).getTime() ? 1 : -1;
			} else {
				if (parseToNumber) {
					return +elementA[sortBy] > +elementB[sortBy] ? 1 : -1;
				}
				return elementA[sortBy] > elementB[sortBy] ? 1 : -1;
			}
		});
	}

	if (order === "DESC") {
		collection = collection.reverse();
	}

	return collection;
}

function sortUsersByRank(users) {
	let newUsers = JSON.parse(JSON.stringify(users)); // Copy by value, not reference
	newUsers = sortCollectionBy(newUsers, { sortBy: "rank", order: "ASC", parseToNumber: true });
	// Sort equally ranked users (999999 rank) alphabetically by fullName

	let rankings = sortCollectionBy(newUsers, {
		order: "ASC",
		parseToNumber: true,
		sortBy: "rank",
	}).map((user) => user.rank);
	rankings = [...new Set(rankings)];
	const sortedUsers = [];
	for (const rank of rankings) {
		let usersWithRank = newUsers.filter((user) => user.rank === rank);
		if (usersWithRank?.length > 1) {
			usersWithRank = sortCollectionBy(usersWithRank, "name");
		}
		usersWithRank.forEach((user) => {
			sortedUsers.push(user);
		});
	}
	return sortedUsers;
}

const orderUsersInPayPeriodByRank = (usersInPayPeriod) => {
	usersInPayPeriod.forEach((user) => {
		if (!user.rank) {
			user.rank = user?.information?.title ? +user.information.title.slice(0, ordinalOffset) : noRanking;
		}
	});

	return sortUsersByRank(usersInPayPeriod);
};

function refreshSidePanel(
	tree = {
		tree: [],
		activeCommissionCycles: [],
		selectedNode: null,
		expandedNodes: [],
	},
	options = {
		useRanking: false,
		toggleTitle: "By Employee",
	},
	actions = {
		setExpanded: null,
		setSelected: null,
	}
) {
	if (tree?.tree?.length && options?.useRanking) {
		const commissionCycleNodesToOrderByRank =
			tree?.tree?.flatMap((year) => year?.list?.filter((month) => allowedStatuses.includes(month?.status)) || []) || [];
		commissionCycleNodesToOrderByRank?.forEach?.((commissionCycleNode) => {
			for (const payPeriodNode of commissionCycleNode.list) {
				payPeriodNode.list = orderUsersInPayPeriodByRank(payPeriodNode.list);
			}
		});
	}
	if (tree?.selectedNode && tree?.selectedNode !== "") {
		const commissionCycleNodesToExpand = expandActiveCommissionCycle(tree?.tree, tree?.activeCommissionCycles, options?.toggleTitle);
		actions?.setExpanded?.(commissionCycleNodesToExpand);
		actions?.setSelected?.(tree?.selectedNode);
	} else {
		actions?.setExpanded?.(expandActiveCommissionCycle(tree?.tree, tree?.activeCommissionCycles, options?.toggleTitle));
	}
}

export { expandActiveCommissionCycle, refreshSidePanel };
